export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  utm_id?: string;
  utm_adgroup?: string;
  // Allow additional UTM parameters with an index signature
  [key: string]: string | undefined;
}

export function extractUTMParams(url: string): UTMParams {
  try {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;

    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_id: params.get('utm_id') || undefined,
      utm_adgroup: params.get('utm_adgroup') || undefined,
      gclid: params.get('gclid') || undefined,
      fbclid: params.get('fbclid') || undefined,
      fbc: params.get('fbc') || undefined,
    };
  } catch (error) {
    return {};
  }
}

export function extractUTMParamsFromReferrer(): UTMParams {
  if (typeof document === 'undefined') return {};
  return extractUTMParams(document.referrer);
}

export function sanitizeUtmValue(value?: string): string | undefined {
  if (!value) return undefined;
  // Allow alphanumeric, dash, underscore, period, slash, and space. Truncate to 255 chars.
  return value.replace(/[^\w\-./\s]/g, '').substring(0, 255);
}

export function sanitizeUTMParams(params: UTMParams): UTMParams {
  // Start with standard parameters
  const sanitized: UTMParams = {
    utm_source: sanitizeUtmValue(params.utm_source),
    utm_medium: sanitizeUtmValue(params.utm_medium),
    utm_campaign: sanitizeUtmValue(params.utm_campaign),
    utm_term: sanitizeUtmValue(params.utm_term),
    utm_content: sanitizeUtmValue(params.utm_content),
    utm_id: sanitizeUtmValue(params.utm_id),
    utm_adgroup: sanitizeUtmValue(params.utm_adgroup),
    gclid: sanitizeUtmValue(params.gclid),
    fbclid: sanitizeUtmValue(params.fbclid),
    fbc: sanitizeUtmValue(params.fbc),
  };

  // Process any additional utm_* parameters
  Object.keys(params).forEach((key) => {
    if (key.startsWith('utm_') && sanitized[key] === undefined) {
      sanitized[key] = sanitizeUtmValue(params[key]);
    }
  });

  return sanitized;
}

export function storeUTMParams(utmParams: UTMParams): void {
  if (typeof localStorage === 'undefined') return;
  const sanitized = sanitizeUTMParams(utmParams);
  localStorage.setItem('utm_params', JSON.stringify(sanitized));
}

export function getStoredUTMParams(): UTMParams {
  if (typeof localStorage === 'undefined') return {};
  try {
    const stored = localStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
}
